import type { ApiError } from '@etnrlz/shared';
import { config } from '../config';

let tokenProvider: () => string | null = () => null;
export function setTokenProvider(fn: () => string | null) {
  tokenProvider = fn;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: ApiError | { message: string },
  ) {
    super(payload.message || `HTTP ${status}`);
    this.name = 'ApiRequestError';
  }
}

interface Opts {
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  kioskToken?: string;
}

async function request<T>(method: string, path: string, opts: Opts = {}): Promise<T> {
  const url = new URL(config.apiBase + path);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = tokenProvider();
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.kioskToken) headers['x-kiosk-token'] = opts.kioskToken;

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    throw new ApiRequestError(res.status, data ?? { message: res.statusText });
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: Opts) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: Opts) => request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Opts) => request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Opts) =>
    request<T>('PATCH', path, { ...opts, body }),
  del: <T>(path: string, opts?: Opts) => request<T>('DELETE', path, opts),
};
